/**
 * A volunteer department — the unit members actually sign up for.
 *
 * Departments live inside an event and are never browsed as a global list.
 * The same card serves three audiences, hence `mode`:
 *   member — sign up / cancel
 *   view   — a past event, read-only
 *   desk   — the admin volunteering desk: view team, remove
 */

import { formatDate } from '../data';
import { Avatar } from './Avatar';
import { Button, Pill } from './ui';

// mode: 'member' | 'view' | 'desk'

export function DeptCard({
  dept,
  db,
  meId,
  mode,
  onJoin,
  onLeave,
  onViewTeam,
  onRemove,
}) {
  const signups = db.signups.filter((s) => s.deptId === dept.id);
  const filled = signups.length;
  const full = filled >= dept.needed;
  const event = db.events.find((e) => e.id === dept.eventId);
  const team = signups
    .map((s) => db.members.find((m) => m.id === s.memberId))
    .filter((m) => !!m);
  const mySignup = meId ? signups.find((s) => s.memberId === meId) : null;
  const mine = !!mySignup;
  /*
   * The volunteer desk confirms a sign-up, and the member had no way to learn
   * it: the card said "You're signed up" for ever, whether or not the desk had
   * accepted them (owner's report, 2 Sep 2026).
   */
  const confirmed = mySignup?.status === 'confirmed';

  // A full team greys out — unless you are on it, in which case it stays
  // live so you can still cancel.
  const dimmed = full && !mine;

  return (
    <div
      className={`mb-2.5 border border-line border-l-[3px] p-4 ${
        dimmed ? 'border-l-line-strong bg-ground' : 'border-l-sea-600 bg-white'
      }`}
    >
      {/* No `flex-wrap` here: with it, a long zone line pushed the "2/8"
          figure under the title on some cards and not others, so a column of
          cards read as misaligned. The text block shrinks and wraps instead. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-market text-lg font-bold leading-tight tracking-[-0.025em]">
            {dept.name}
          </div>
          <div className="mt-1 font-market text-[9.5px] text-copy-label">
            {event ? `${event.title} · ${formatDate(event.date)}` : ''} · {dept.zone}
          </div>
        </div>
        <div className="flex-none text-right">
          <div
            className={`font-market text-[22px] font-extrabold leading-none tabular-nums ${
              full ? 'text-copy-label' : 'text-sea-600'
            }`}
          >
            {filled}/{dept.needed}
          </div>
          <div className="mt-0.5 font-market text-[8.5px] uppercase tracking-[0.1em] text-copy-label">
            Filled
          </div>
        </div>
      </div>

      <div className="mb-2 mt-2.5 h-1.5 overflow-hidden rounded-full bg-line-chip">
        <i
          className={`block h-full rounded-full transition-all duration-500 ${
            full ? 'bg-line-strong' : 'bg-sea-600'
          }`}
          style={{ width: `${Math.min(100, (filled / dept.needed) * 100)}%` }}
        />
      </div>

      <div className="font-market text-[10.5px] leading-relaxed text-copy-muted">
        {dept.task}
        <br />
        <b>Lead:</b> {dept.lead} · <b>{dept.hours} hrs credit</b>
      </div>

      {team.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {team.map((m) => (
            <Avatar key={m.id} member={m} className="h-7 w-7 border-2 border-white text-[10px]" />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {mode === 'member' &&
          (mine ? (
            <>
              <Pill tone="ok">
                {confirmed ? '✓ Confirmed by the desk' : '✓ Signed up · awaiting confirmation'}
              </Pill>
              <Button tone="g" small onClick={() => onLeave?.(dept.id)}>
                Cancel
              </Button>
            </>
          ) : full ? (
            <Pill tone="late">Team full</Pill>
          ) : (
            <Button tone="v" small onClick={() => onJoin?.(dept.id)}>
              Sign up
            </Button>
          ))}

        {mode === 'view' && <Pill>Event finished</Pill>}

        {mode === 'desk' && (
          <>
            <Button tone="g" small onClick={() => onViewTeam?.(dept.id)}>
              View team
            </Button>
            <Button tone="g" small onClick={() => onRemove?.(dept.id)}>
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
